import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FolderKanban, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";

const statusColors = {
  planning: "bg-slate-100 text-slate-700",
  active: "bg-blue-100 text-blue-700",
  on_hold: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};

export default function ProjectsWidget() {
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list("-updated_date"),
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const calculateProgress = (projectId) => {
    const projectTasks = allTasks.filter(t => t.project_id === projectId);
    if (projectTasks.length === 0) return 0;
    const completed = projectTasks.filter(t => t.status === "completed").length;
    return (completed / projectTasks.length) * 100;
  };

  if (isLoading) {
    return (
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-20 bg-slate-100 rounded" />
          <div className="h-20 bg-slate-100 rounded" />
        </div>
      </Card>
    );
  }

  const activeProjects = projects.filter(p => p.status === "active" || p.status === "planning");

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FolderKanban className="w-5 h-5 text-zinc-900" />
          <h3 className="font-semibold text-slate-900">Prosjektoversikt</h3>
        </div>
        <Link to={createPageUrl("Projects")}>
          <Button variant="ghost" size="sm">
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
      
      {activeProjects.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Ingen aktive prosjekter</p>
      ) : (
        <div className="space-y-3">
          {activeProjects.slice(0, 4).map(project => (
            <Link key={project.id} to={createPageUrl("ProjectDetail") + `?id=${project.id}`}>
              <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <p className="font-medium text-slate-900 text-sm">{project.name}</p>
                  <Badge className={`text-xs border-0 ${statusColors[project.status]}`}>
                    {project.status}
                  </Badge>
                </div>
                <Progress value={calculateProgress(project.id)} className="h-1.5" />
                <p className="text-xs text-slate-500 mt-1">
                  {Math.round(calculateProgress(project.id))}% fullført
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}